import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { Box, Copy, DeleteWarning, Loading, SystemText } from '@salesperformanceint/e4shared';
import PageHeader from 'components/general/pageHeader';
import BlobImage from 'components/blobImage';
import Assets from 'components/general/assets';
import Like from 'components/general/like';
import CompetencyList from 'components/general/competencyList';
import ManagePost from '../blog/components/managePost';

import { loadProfile } from 'actions/profile';
import { pageViewed } from 'actions/ui';
import { loadPost, loadPostFromGuid, deleteComment } from 'actions/blog';
import { isFuture, e4Format, e4Formats } from 'helpers/dates';
import { hasModule } from 'helpers/profile';

import ManageComment from './components/manageComment';

import './blogPost.css';
import config from 'config';

export const UserBlogPost = ({ match, history }) => {
    const [showManageComment, setShowManageComment] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [showManagePost, setShowManagePost] = useState(false);
    const [comment, setComment] = useState();
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);
    const profile = useSelector(state => state.profile.profile);
    const post = useSelector(state => state.blog.post);
    const error = useSelector(state => state.blog.error);

    const deleteHandler = confirm => {
        setShowDelete(false);
        setDeleting(null);
        if (confirm) {
            const comment = post.comments.filter(c => c.id === deleting)[0];
            dispatch(deleteComment(comment, () => loadPostHandler()));
        }
    };

    useEffect(() => {
        const { post } = match.params;
        loadPostHandler();
        post && dispatch(pageViewed({ type: 'BLP', type_Id: post }));
    }, [match.params.post]);

    useEffect(() => {
        if (post?.owner_Id) {
            dispatch(loadProfile(post?.owner_Id));
        }
    }, [post?.owner_Id]);

    useEffect(() => {
        if (error) {
            history.replace('/error');
        }
    }, [error]);

    const loadPostHandler = () => {
        const { post, guid } = match.params;
        post && dispatch(loadPost(post));
        guid && dispatch(loadPostFromGuid(guid));
    };

    const { post: postId, guid } = match.params;
    const safePost = post || {};
    if (safePost?.post) safePost.post = safePost.post.replace(/\r/g, '').replace(/\n/g, '');
    if (!hasModule(user, 'blogs')) history.replace('/');

    const commentCount = safePost.comments ? safePost.comments.length : 0;
    const canEdit = safePost.owner_Id === user.id || user.administrator;
    const loading = !post?.owner_Id && (!safePost.id || postId !== safePost.id.toString());

    const addNewButton = () =>
        canEdit ? (
            <button className="btn btn-primary" onClick={() => setShowManagePost(true)}>
                <i className="fa fa-pencil mr10" />
                {SystemText('BlogPost/Buttons/Edit')}
            </button>
        ) : null;

    return (
        <div>
            <PageHeader
                title={loading ? SystemText('/BlogPost/Title') : safePost.title}
                extra={addNewButton}
                useProfile={safePost.owner_Id !== user.id}
            />
            <section className="content blog-post" data-testid="blogpost-content">
                <div className="row">
                    <div className="col-xs-12 mt10">
                        <Box>
                            {loading ? (
                                <div data-testid="loading">
                                    <Loading />
                                </div>
                            ) : (
                                <div>
                                    {(!safePost.published || isFuture(safePost.date)) && !guid && (
                                        <div className="row">
                                            <div className="col-md-12 mb10">
                                                {!safePost.published && (
                                                    <span className="label label-danger mr10">
                                                        {SystemText('BlogPost/NotPublished')}
                                                    </span>
                                                )}
                                                {isFuture(safePost.date) && (
                                                    <span className="label label-danger mr10">
                                                        {SystemText('BlogPost/Embargo')}
                                                    </span>
                                                )}
                                                <div className="text-sm mt10">
                                                    {SystemText('BlogPost/Share')}: {config.url}blog-share/
                                                    {safePost.guid}
                                                </div>
                                                <hr />
                                            </div>
                                        </div>
                                    )}
                                    <p>
                                        {SystemText('BlogPost/By')}{' '}
                                        <Link to={`/blog/${safePost.owner_Id}`}>{safePost.owner_Full_Name}</Link>
                                        <Link
                                            className="btn ml10 btn-xs btn-success hidden-xs"
                                            to={`/blog/${safePost.owner_Id}`}
                                        >
                                            <i className="fa fa-comment mr5" />
                                            {SystemText('BlogPost/ViewAll', safePost.owner_First_Name)}
                                        </Link>
                                    </p>
                                    <p>{e4Format(safePost.date, e4Formats.longWithDay)}</p>
                                    <p>
                                        <span className="mr10">
                                            <i className="fa fa-comments" /> {commentCount}
                                        </span>
                                        <i className="fa fa-thumbs-up" /> {safePost.like_Count}
                                    </p>
                                    <p>
                                        <Copy value={safePost.description} />
                                    </p>
                                    {safePost.competencies && safePost.competencies.length > 0 && (
                                        <div data-testid="competencies" className="mt10 mb10">
                                            <CompetencyList competencies={safePost.competencies} />
                                        </div>
                                    )}
                                    {safePost.blog_Image && (
                                        <div className="mb20 blog-image-holder">
                                            <BlobImage
                                                key={`post_${safePost.blog_Image}`}
                                                alt=""
                                                folder="blog_Image"
                                                src={safePost.blog_Image}
                                            />
                                        </div>
                                    )}
                                    <p>
                                        <Copy value={safePost.post} />
                                    </p>
                                    {safePost.assets && <Assets assets={safePost.assets} />}
                                    <div className="clear mb10">
                                        <Like
                                            entity="blogpost"
                                            disabled={!safePost.published}
                                            id={safePost.id}
                                            name="post"
                                            owner_Id={safePost.owner_Id}
                                        />
                                    </div>
                                    {safePost.tags && (
                                        <p>
                                            {safePost.tags.map(tag => (
                                                <Link key={`tag-${tag}`} to={`/search?tag=${tag}`}>
                                                    <span className="badge mr10">{tag}</span>
                                                </Link>
                                            ))}
                                        </p>
                                    )}
                                    <div className="comment-header mt20">
                                        <h3>
                                            {SystemText('BlogPost/Comments')} ({commentCount})
                                        </h3>
                                    </div>
                                    <button
                                        className="mb20 btn btn-success"
                                        disabled={!safePost.published}
                                        onClick={() => setShowManageComment(true)}
                                    >
                                        {SystemText('BlogPost/Buttons/AddComment')}
                                    </button>
                                    {commentCount === 0 && (
                                        <div className="row mb10">
                                            <div className="col-md-12">{SystemText('BlogPost/NoComments')}</div>
                                        </div>
                                    )}
                                    {safePost.comments?.map(comment => {
                                        return (
                                            <div
                                                data-testid="comment"
                                                key={`comment-${comment.id}`}
                                                className="user-comment mb10"
                                            >
                                                <div>
                                                    <BlobImage
                                                        folder="avatar"
                                                        initials={comment.owner_Initials}
                                                        alt={comment.owner_Full_Name}
                                                        src={comment.avatar}
                                                        key={comment.avatar}
                                                    />
                                                </div>
                                                <div>
                                                    <div className="mb5">
                                                        <b>{comment.owner_Full_Name}</b>
                                                        {!comment.deleted && comment.owner_Id === user.id && (
                                                            <button
                                                                onClick={() => {
                                                                    setShowDelete(true);
                                                                    setDeleting(comment.id);
                                                                }}
                                                                title={SystemText('BlogPost/Buttons/DeleteComment')}
                                                                className="ml10 btn btn-link btn-xs"
                                                            >
                                                                <i className="fa fa-trash text-danger" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="mb5">
                                                        {e4Format(comment.added_Date, e4Formats.longWithDay, true)}
                                                    </div>
                                                    <div className="mb5">
                                                        {comment.deleted ? (
                                                            <span className="italic">
                                                                {SystemText('BlogPost/CommentDeleted')}
                                                            </span>
                                                        ) : (
                                                            <div>
                                                                <Copy value={comment.comment} />
                                                                <div className="mt5">
                                                                    <Like
                                                                        small
                                                                        entity="blogcomment"
                                                                        name="comment"
                                                                        id={comment.id}
                                                                        disabled={!safePost.published}
                                                                        owner_Id={comment.owner_Id}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Box>
                    </div>
                </div>
            </section>
            <DeleteWarning
                message={SystemText('BlogPost/DeleteComment/Warning')}
                show={showDelete}
                close={confirm => deleteHandler(confirm)}
            />
            <ManageComment
                show={showManageComment}
                close={() => {
                    setShowManageComment(false);
                    setComment(null);
                }}
                reload={() => loadPostHandler()}
                post={safePost}
                commentProps={comment}
            />
            <ManagePost
                show={showManagePost}
                close={() => setShowManagePost(false)}
                reload={() => loadPostHandler()}
                post={safePost}
            />
        </div>
    );
};

export default withRouter(UserBlogPost);
